import React, { useMemo, useCallback, useState } from 'react';
import Popper from '@mui/base/Popper';
import { Box, styled } from '@mui/system';
import Checkbox from '@mui/material/Checkbox';
import { default as MUISelect } from '@mui/base/Select';
import { useTheme } from '@mui/material/styles';

import { X as CloseIcon, ChevronDown as UnfoldMoreRoundedIcon, ListFilter as FilterListIcon } from 'lucide-react';
import Text from '@root/components/v2/ui-elements/Text';
import './style.scss';
import Tooltip from '../Tooltip';
import ComplianceIconMapping from '@root/components/complianceIconMapping';
import { RANDOM_COLORS } from '@root/utilities/colors';
import RegionsFlagIcons from '../CountryFlagIcons/regionFlagIcons';

const cachedColors = {};

const grey = {
  50: '#f6f8fa',
  100: '#eaeef2',
  200: '#d0d7de',
  300: '#afb8c1',
  400: '#8c959f',
  500: '#6e7781',
  600: '#57606a',
  700: '#424a53',
  800: '#32383f',
  900: '#24292f',
};

// Add dark mode colors
const darkModeColors = {
  background: '#1a202c',
  border: '#4a5568',
  text: '#f7fafc',
  hoverBg: '#2d3748',
  selectedBg: '#2c5282',
  chipBg: '#2d3748',
};

const Chip = ({ options, value, onRemove, fromSbomExport }) => {
  // console.log(options, value);
  const option = useMemo(() => {
    return options.find(o => o.value === value);
  }, [options, value]);

  const handleRemoveClick = useCallback(
    e => {
      e.stopPropagation();

      if (onRemove) {
        onRemove(value);
      }
    },
    [onRemove, value],
  );

  return (
    <Box className={`Select__Chip ${fromSbomExport && 'Sbom__Chip'}`}>
      <Text className={`Select__Chip__Text ${fromSbomExport && 'Sbom__Chip__Text'}`}>{option?.label}</Text>
      <button className="Select__Chip__Button" onClick={handleRemoveClick}>
        <CloseIcon size={16} />
      </button>
    </Box>
  );
};

const Button = React.forwardRef(function Button(props, ref) {
  const { ownerState, className, regionDropdown, label, ...other } = props;
  const { isDarkMode } = useTheme();

  const style = useMemo(() => {
    return {
      ...(ownerState.width && { width: ownerState.width, minWidth: ownerState.minWidth }),
      ...(ownerState.height && { height: ownerState.height }),
    };
  }, [ownerState]);
  // console.log(props, 'nvj');
  return regionDropdown ? (
    <button
      className={`${className} Select__Button_CommonComponent_region ${ownerState.disabled ? 'Disabled' : ''} ${
        ownerState.complianceRuleAction ? 'ComplianceRuleActive' : ''
      }`}
      type="button"
      style={{
        ...style,
        backgroundColor: props.transparentBg ? 'transparent' : isDarkMode ? darkModeColors.background : null,
      }}
      {...other}
      ref={ref}>
      <Tooltip title={label ? label : 'Region'} isIcon={true}>
        <Box
          // disabled={!addResourceTag}
          className="text font-12 weight-600"
          sx={{
            fill: 'var(--tabbar-icons-color)',

            /* Boder & Line/Light */
            display: 'flex',
            padding: '3px 10px',
            // border: '1px solid var(--border-color-resource)',
            // boxShadow: '0px 8px 18px rgba(93, 106, 131, 0.05)',
            // margin: '0 3px 0 10px',
            borderRadius: '5px',
            cursor: 'pointer',
            transition: 'all 0s',
            alignItems: 'center',
            // background: ownerState?.value.length > 0 ? '#e6efff' : '#fff',

            '&:hover': {
              // fill: 'white !important',
              // background: '#e2e8f0',
              // path: {
              //   fill: 'white !important',
              // },
            },
          }}>
          <FilterListIcon size={20} />
          {ownerState?.value?.length && ownerState?.value?.length !== 0 ? (
            <span style={{ marginLeft: '5px' }}>{ownerState?.value?.length}</span>
          ) : null}
        </Box>
      </Tooltip>
    </button>
  ) : (
    <button
      className={`${className} Select__Button_CommonComponent ${ownerState.disabled ? 'Disabled' : ''} ${
        ownerState.complianceRuleAction ? 'ComplianceRuleActive' : ''
      }`}
      type="button"
      style={{
        ...style,
        backgroundColor: props.transparentBg ? 'transparent' : isDarkMode ? darkModeColors.background : null,
      }}
      {...other}
      ref={ref}>
      <Text className="Select__ButtonText">{ownerState.renderValue(ownerState.value) ?? ownerState.placeholder}</Text>
      <UnfoldMoreRoundedIcon size={20} />
    </button>
  );
});

// added custom scroll bar css
const StyledListbox = styled('ul')(
  ({ theme }) => `
  font-family: Manrope;
  font-size: 0.875rem;
  box-sizing: border-box;
  margin: 0;
  padding: 0;
  // margin: 12px 0;
  // min-width: 184px;
  border-radius: 5px;
  overflow: auto;
  outline: 0px;
  list-style: none;
  background: ${theme.palette.mode === 'dark' ? darkModeColors.background : '#fff'};
  border: 1px solid ${theme.palette.mode === 'dark' ? darkModeColors.border : '#CBD5E0'};
  color: ${theme.palette.mode === 'dark' ? darkModeColors.text : grey[900]};
  box-shadow: 0px 8px 18px rgba(0, 0, 0, ${theme.palette.mode === 'dark' ? '0.3' : '0.1'});
   
   &::-webkit-scrollbar-track {
    border-radius: 8px;
    background-color: ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)'};
  }

  &::-webkit-scrollbar {
    width: 8px;
    height: 8px;
    border-radius: 8px;
    background-color: ${theme.palette.mode === 'dark' ? '#2d3748' : '#f5f5f5'};
  }

  &::-webkit-scrollbar-thumb {
    border-radius: 8px;
    background-color: ${theme.palette.mode === 'dark' ? '#4a5568' : '#ccc'};

    &:hover {
      background-color: ${theme.palette.mode === 'dark' ? '#718096' : '#aaa'} !important;
    }
  }
  `,
);

const CustomSelect = React.forwardRef(function CustomSelect(props, ref) {
  const { regionDropdown, fromCompliance } = props;

  const MyPopper = useCallback(
    props => {
      return (
        <Popper
          {...props}
          container={props.ownerState.isTagModalContainer ? document.querySelector('.create-tag-modal-container') : ''}
          style={{
            width: regionDropdown && !fromCompliance ? '200px' : fromCompliance ? '250px' : props.anchorEl?.clientWidth,
            marginTop: '10px',
          }}
          placement="bottom-start"
          // use this to handle select css
          // open={true}
          disablePortal={false}
          disableInteractive={true}
          modifiers={[
            {
              name: 'flip',
              enabled: false,
            },
            {
              name: 'preventOverflow',
              enabled: true,
              options: {
                boundary: 'viewport',
                padding: 38,
              },
            },
            {
              name: 'offset',
              options: {
                offset: [0, 10],
              },
            },
          ]}
          className={
            props.ownerState.inventoryTableDropDown
              ? 'popper-resource-table-dropdown'
              : props.ownerState.regionDropdown
                ? 'popper-zindex'
                : props.ownerState.isTagModalContainer
                  ? 'tag-popper-zindex'
                  : props.ownerState.isKubernetesCompliance
                    ? 'KubernetesCompliance_Filter'
                    : 'normal-zindex'
          }
        />
      );
    },
    [fromCompliance, regionDropdown],
  );
  const slots = useMemo(() => {
    // console.log(props, 'nkdjnfdk');
    return {
      root: Button,
      listbox: StyledListbox,
      popper: MyPopper,
      ...props.slots,
    };
  }, [MyPopper, props]);

  return <MUISelect {...props} ref={ref} slots={slots} transparentBg={props.transparentBg} sx={{ marginTop: '2px' }} />;
});

const colorMapper = element => {
  if (cachedColors[`${element}`]) {
    return cachedColors[`${element}`];
  }

  const color = RANDOM_COLORS[Math.floor(Math.random() * RANDOM_COLORS?.length)];
  cachedColors[`${element}`] = color;
  return color;
};

function Select({
  placeholder,
  options = [],
  value,
  enableSelectAll,
  allSelectedButtonLabel,
  width,
  height = '29px',
  minWidth,
  onChange,
  mode = 'multiple',
  enableChips = false,
  label,
  disabled = false,
  transparentBg,
  complianceRuleAction = false,
  showCheckBox = true,
  regionDropdown = false,
  isTagModalContainer = false,
  inventoryTableDropDown = false,
  isTooltipActive = true,
  isIcon = false,
  isLabelTagging = false,
  fromCompliance = false,
  fromSbomExport = false,
  disableCheckBoxes = false,
  isKubernetesCompliance = false,
  jira_input,
}) {
  const { isDarkMode } = useTheme();

  // key is used to force the Select component to be recreated. As a workaround to force it to close in mode === 'single'
  const [isOpen, setIsOpen] = useState(false);
  // const [key, setKey] = useState(Date.now());
  const isAllSelected = useMemo(() => {
    if (!value || !value?.length) return false;

    return options?.every(o => value.find(v => v === o.value));
  }, [options, value]);
  // console.log(options, 'hfjfh');
  const handleChipRemove = useCallback(
    v => {
      onChange(value.filter(_v => _v !== v));
    },
    [onChange, value],
  );

  const renderValue = useCallback(
    value => {
      if (!options?.length || !value || (Array.isArray(value) && !value?.length)) {
        return (
          <span className={`${jira_input ? 'jira_input' : ''} ${isDarkMode ? 'dark-mode' : ''}`}>
            {placeholder || 'Select'}
          </span>
        );
      }

      const firstOption = options.find(o => o.value === value[0]);

      if (!firstOption) return null;

      if (Array.isArray(value) && value?.length) {
        return (
          <div className="Select__SelectedValueContainer">
            {allSelectedButtonLabel && isAllSelected ? (
              <Text
                element="span"
                font="medium"
                className={jira_input ? 'jira_input' : fromSbomExport ? 'sbom-chip' : ''}>
                {allSelectedButtonLabel}
              </Text>
            ) : (
              <>
                {enableChips ? (
                  <>
                    {value.slice(0, 2).map(v => (
                      <Chip value={v} options={options} onRemove={handleChipRemove} fromSbomExport={fromSbomExport} />
                    ))}

                    <Text font="semi-bold" className="Select__SelectedValue__Count">
                      {value?.length > 2 ? `+${value?.length - 2}` : null}
                    </Text>
                  </>
                ) : (
                  <>
                    {isTooltipActive ? (
                      <Tooltip title={firstOption.label} isIcon={true}>
                        <Text element="span" font="medium" className={`SingleLine ${jira_input ? 'jira_input' : ''}`}>
                          {firstOption.label}
                        </Text>
                      </Tooltip>
                    ) : (
                      <Text element="span" font="medium" className={`${jira_input ? 'jira_input' : ''}`}>
                        {firstOption.label}
                      </Text>
                    )}

                    <Text font="semi-bold" className="Select__SelectedValue__Count">
                      {value?.length > 1 ? `+${value?.length - 1}` : null}
                    </Text>
                  </>
                )}
              </>
            )}
          </div>
        );
      }

      return <span>{firstOption.label}</span>;
    },
    [
      options,
      jira_input,
      isDarkMode,
      placeholder,
      allSelectedButtonLabel,
      isAllSelected,
      enableChips,
      isTooltipActive,
      handleChipRemove,
      fromSbomExport,
    ],
  );

  const handleDisableCondition = useCallback(option => {
    return option.isAlreadySelected;
  }, []);

  const handleOptionClick = useCallback(
    option => {
      if (disableCheckBoxes && option.isAlreadySelected) {
        return;
      }
      if (mode === 'single') {
        onChange([option.value]);
        setIsOpen(false); // Trick to force the CustomSelect component to "close" by re-creating it. TODO: Find a proper solution since the `open` prop does not work.
      } else {
        if (option === 'all') {
          if (isAllSelected) {
            onChange([]);
          } else {
            onChange(options.map(o => o.value));
          }
        } else {
          if (value.includes(option.value)) {
            onChange(value.filter(v => v !== option.value));
          } else {
            onChange([...value, option.value]);
          }
        }
      }
    },
    [disableCheckBoxes, mode, onChange, isAllSelected, options, value],
  );

  return (
    <CustomSelect
      // key={key}
      value={value}
      placeholder={placeholder}
      renderValue={renderValue}
      regionDropdown={regionDropdown}
      fromCompliance={fromCompliance}
      inventoryTableDropDown={inventoryTableDropDown}
      label={label}
      isTagModalContainer={isTagModalContainer}
      isKubernetesCompliance={isKubernetesCompliance}
      multiple
      width={width}
      height={height}
      minWidth={minWidth}
      transparentBg={transparentBg}
      complianceRuleAction={complianceRuleAction}
      disabled={disabled}
      open={isOpen}
      onOpen={() => setIsOpen(true)}
      onClose={() => setIsOpen(false)}
      sx={{
        marginTop: '2px',
        '& .Select__Option': {
          backgroundColor: isDarkMode ? darkModeColors.background : '#fff',
          '&:hover': {
            backgroundColor: isDarkMode ? darkModeColors.hoverBg : '#f7fafc',
          },
          '&.Selected': {
            backgroundColor: isDarkMode ? darkModeColors.selectedBg : '#e6efff',
          },
        },
        '& .Select__Chip': {
          backgroundColor: isDarkMode ? darkModeColors.chipBg : '#e6efff',
          color: isDarkMode ? darkModeColors.text : 'inherit',
        },
      }}>
      {!options?.length ? (
        <Box padding="24px 16px" textAlign="center">
          <Text font="italic">No options</Text>
        </Box>
      ) : null}
      <div
        style={
          regionDropdown
            ? { maxHeight: '280px', padding: '10px' }
            : {
                minHeight: `${Math.min(options?.length, 7) * 35}px`,
                maxHeight: `280px`,
              }
        }>
        {enableSelectAll && options?.length ? (
          <div value={'all'} disabled className="Select__Option" onClick={() => handleOptionClick('all')}>
            <Text element="div" className="Select__OptionLabel" font="semi-bold">
              All
            </Text>
            {mode === 'multiple' ? (
              <Checkbox
                sx={{
                  '&.Mui-checked': {
                    color: '#9362d9',
                  },
                }}
                className="Select__OptionCheckbox"
                checked={isAllSelected}
              />
            ) : null}
          </div>
        ) : null}
        <div style={{ maxHeight: '300px', zIndex: '100' }}>
          {options.map(option => (
            <div
              key={option.value}
              value={option.value}
              className={`Select__Option ${value.includes(option.value) ? 'Selected' : ''}`}
              onClick={() => handleOptionClick(option)}>
              {isIcon ? (
                fromCompliance ? (
                  <ComplianceIconMapping style={{ width: '20px', marginRight: '12px' }} name={option.value} />
                ) : regionDropdown ? (
                  <RegionsFlagIcons name={option.value} isRounded={true} />
                ) : (
                  ''
                )
              ) : (
                ''
              )}
              {isTooltipActive ? (
                <Tooltip title={option.label}>
                  <Text
                    style={{
                      width: '150px',
                    }}
                    className=" SingleLine Select__OptionLabel"
                    font="semi-bold">
                    {option.label === 'Google Cloud Architecture Framework' ? 'GCAF' : option.label}{' '}
                  </Text>
                </Tooltip>
              ) : (
                <Text element="div" className="Select__OptionLabel" font="semi-bold">
                  {option.label === 'Google Cloud Architecture Framework' ? 'GCAF' : option.label}
                </Text>
              )}
              {isLabelTagging && (
                <Tooltip title={option.parent} isIcon={true}>
                  <span
                    className="m-0 SingleLine"
                    style={{
                      // height: '20px',
                      width: '80px',
                      borderRadius: '20px',
                      // padding: '8px',
                      color: '#fff',
                      textAlign: 'center',
                      backgroundColor: colorMapper(option.parent),
                    }}>
                    {option.parent}
                  </span>
                </Tooltip>
              )}
              {showCheckBox ? (
                <Checkbox
                  sx={{
                    '&.Mui-checked': {
                      color: '#9362d9',
                    },
                    '&.Mui-disabled': {
                      color: '#bdbdbd', // light gray for disabled state
                    },
                  }}
                  disabled={disableCheckBoxes ? handleDisableCondition(option) : false}
                  checked={
                    disableCheckBoxes
                      ? handleDisableCondition(option) || value.includes(option.value)
                      : value.includes(option.value)
                  }
                  className="Select__OptionCheckbox"
                  style={{ visibility: mode === 'multiple' ? 'visible' : 'hidden' }}
                />
              ) : null}
            </div>
          ))}
        </div>
      </div>
    </CustomSelect>
  );
}

export default Select;
